import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type { ConsentViewT } from "@/types/database/consents";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ClipboardSignature } from "lucide-react";
import useConsentDialog from "./hooks/useConsentDialog";

interface ConsentDialogProps {
	diaryId?: string;
	isLoading?: boolean;
}

const ConsentDialog: React.FC<ConsentDialogProps> = ({
	diaryId,
	isLoading = false,
}) => {
	const { showDialog, unsignedConsents } = useConsentDialog({
		diaryId,
		isLoading,
	});

	// If no unsigned consents or still loading, return null
	if (!showDialog) {
		return null;
	}

	return (
		<Dialog defaultOpen={true} modal={true}>
			<DialogContent className="bg-amber-50 border-amber-200 sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-amber-700">
						<AlertCircle className="w-5 h-5 text-amber-600" />
						Action Required: Sign Consent
					</DialogTitle>
					<DialogDescription className="font-medium text-amber-700">
						You must sign the following consent form
						{unsignedConsents.length > 1 ? "s" : ""} before continuing:
					</DialogDescription>
				</DialogHeader>

				{unsignedConsents.length === 1 ? (
					<Button
						asChild
						size="sm"
						className="bg-amber-600 hover:bg-amber-700 text-white"
					>
						<Link
							to="/preview/consent/$consentId"
							params={{
								consentId: unsignedConsents[0]?.consent_id || "",
							}}
						>
							<ClipboardSignature size={16} className="mr-2" />
							Sign Consent Now
						</Link>
					</Button>
				) : (
					<div className="space-y-2">
						{unsignedConsents.map((consent) => (
							<Button
								key={consent.consent_id || ""}
								asChild
								size="sm"
								className="justify-between bg-amber-600 hover:bg-amber-700 w-full text-white"
							>
								<Link
									to="/preview/consent/$consentId"
									params={{
										consentId: consent.consent_id || "",
									}}
								>
									{consent.consent_name || "Consent Form"}
									<ClipboardSignature size={16} className="ml-2" />
								</Link>
							</Button>
						))}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default ConsentDialog;
